import React, { ReactNode } from "react";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { convertToBgImage, BgImage } from "gbimage-bridge";
import styled from "styled-components";
import { SanityImage } from "sanity-image";
import { MOBILE_BREAKPOINT } from "@util/constants";

const StyledImage = styled(GatsbyImage) <{
  width?: string;
}>`
  ${({ width }) => (width ? `width:${width}` : "width: 100%;")}

  @media only screen and (min-width: ${MOBILE_BREAKPOINT}px) {
    ${({ width }) => (width ? `width:${width}` : "width: 100%;")}
  }
`;

const backgroundStyle = (zoomanimation?: boolean | null | undefined) => `
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    @keyframes zoomIn {
      from {
        width: 100%;
        height: 100%;
      }
      to {
        width: 150%;
        height: 150%;
      }
    }
    ${zoomanimation && `
      animation-name: zoomIn;
      animation-duration: 20s;`};
  }
`

const BackgroundGatsbyImage = styled(GatsbyImage) <{ zoomanimation?: boolean | null }>`
  ${({ zoomanimation }) => backgroundStyle(zoomanimation)}
`;

const BackgroundSanityImage = styled(SanityImage) <{ zoomanimation?: boolean | null }>`
  ${({ zoomanimation }) => backgroundStyle(zoomanimation)}
`;

const BackgroundImageWrapper = styled.div<{ minHeight?: string, objectFit?: "contain" | "cover" | "fill" | "scale-down" | "none" }>`
  width: 100%;
  height: 100%;
  z-index: 5;
  margin: 0;
  overflow: hidden;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`};
  img {
    ${({ objectFit }) => objectFit && `object-fit: ${objectFit};`};
  }
`;

interface Props {
  image?: IGatsbyImageData;
  staticImage?: string;
  objectFit?: "contain" | "cover" | "fill" | "scale-down" | "none";
  minHeight?: string;
  isBackground?: boolean;
  backgroundStyle?: any;
  width?: string;
  height?: string;
  altText?: string;
  children?: ReactNode;
  zoomAnimationOn?: boolean;
  quality?: number;
  imageID?: string | null;
}

const Image = (props: Props) => {
  const { minHeight } = props;

  if (props.isBackground) {
    const bgImage = convertToBgImage(props.image);
    return (
      <BgImage preserveStackingContext {...bgImage}>
        <BackgroundImageWrapper minHeight={props.minHeight} objectFit={props.objectFit ?? "cover"}>
          {props.imageID ?
            <BackgroundSanityImage
              projectId="lk4ngv76"
              dataset="production"
              id={props.imageID}
              width={props.quality || 250}
              alt={props.altText}
              zoomanimation={props.zoomAnimationOn}
            /> :
            <BackgroundGatsbyImage
              image={props.image as IGatsbyImageData}
              alt="bg-image"
              zoomanimation={props.zoomAnimationOn} />
          }
          <BackgroundImageWrapper>{props.children}</BackgroundImageWrapper>
        </BackgroundImageWrapper>
      </BgImage>
    );
  }

  if (props.staticImage) {
    return (
      <img
        src={props.staticImage}
        width={props.width}
        height={props.height}
        style={{ minHeight }}
        itemProp="image"
        alt={props.altText ?? "image"}
      />
    );
  }

  if (props.quality && props.imageID) {
    return (
      <SanityImage
        projectId="lk4ngv76"
        dataset="production"
        id={props.imageID}
        width={props.quality}
        alt={props.altText}
        style={{ objectFit: props.objectFit ?? "initial", width: props.width || "100%", height: props.height || "100%" }}
      />
    );
  }

  if (props.image) {
    return (
      <StyledImage
        image={props.image}
        imgStyle={{
          objectFit: props.objectFit ?? "initial",
        }}
        style={{ minHeight, width: props.width }}
        width={props.width}
        itemProp="image"
        alt={props.altText ?? "gatsby-image"}
      />
    );
  }

  return null;
};

export default Image;
